/**
 * Loop topology — the console's copy.
 *
 * The hub owns this derivation (`ops/loopTopology.ts`); everything that
 * executes reads it there. This copy exists for ONE reason: body tinting has
 * to answer "which loop owns this step?" while the user is dragging edges
 * around, before anything is saved. A round trip per canvas edit is not a
 * design, so the canvas derives it locally.
 *
 * A copy is only sanctioned while something proves it is still a copy.
 * `loopTopologyParity.test.ts` imports the HUB'S ACTUAL FUNCTIONS and asserts
 * they agree with these on a table of graph shapes — not a text comparison, and
 * not a second hand-written expectation table. If the hub's membership rule
 * changes and this file does not, that gate fails.
 *
 * The membership rule, and the trap in it:
 *
 *     body(S, E) = descendants(S) \ ({E} ∪ descendants(E))
 *
 * NOT `descendants(S) ∩ ancestors(E)`. A terminal step inside a loop — a
 * persist writing one row-set per pass, a watch — is a descendant of the start
 * but not an ancestor of the end, so that formulation drops it out of the body
 * silently. The constraint that a value may only leave through the end step
 * lives on EDGES, in the hub validator, never on membership.
 */

export type LoopGraphNode = { id?: unknown; type?: unknown; config?: unknown };
export type LoopGraphEdge = { edge_type?: unknown; from_node?: unknown; to_node?: unknown };
export type LoopGraph = { nodes?: unknown; edges?: unknown };

export type LoopPair = { startId: string; endId: string };

function isRec(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

export function loopGraphNodes(graph: LoopGraph | null | undefined): Map<string, LoopGraphNode> {
  const out = new Map<string, LoopGraphNode>();
  const raw = graph?.nodes;
  if (!Array.isArray(raw)) return out;
  for (const n of raw) {
    if (!isRec(n)) continue;
    const id = typeof n.id === "string" ? n.id : "";
    if (!id) continue;
    out.set(id, n as LoopGraphNode);
  }
  return out;
}

function graphEdges(graph: LoopGraph | null | undefined): LoopGraphEdge[] {
  const raw = graph?.edges;
  return Array.isArray(raw) ? (raw.filter(isRec) as LoopGraphEdge[]) : [];
}

/** Forward adjacency over `data` edges. Scope edges confer no membership. */
export function buildForwardAdjacency(edges: ReadonlyArray<LoopGraphEdge>): Map<string, string[]> {
  const forwardAdj = new Map<string, string[]>();
  for (const edge of edges) {
    if (edge.edge_type !== "data") continue;
    const from = typeof edge.from_node === "string" ? edge.from_node : "";
    const to = typeof edge.to_node === "string" ? edge.to_node : "";
    if (!from || !to) continue;
    const list = forwardAdj.get(from) ?? [];
    list.push(to);
    forwardAdj.set(from, list);
  }
  return forwardAdj;
}

export function descendants(forwardAdj: Map<string, string[]>, startId: string): Set<string> {
  const visited = new Set<string>();
  const queue = [startId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const child of forwardAdj.get(current) ?? []) {
      if (visited.has(child)) continue;
      visited.add(child);
      queue.push(child);
    }
  }
  return visited;
}

export function loopStartRefOf(node: LoopGraphNode | undefined | null): string {
  const cfg = node && isRec(node.config) ? node.config : null;
  const ref = cfg?.loop_start_node_id;
  return typeof ref === "string" ? ref.trim() : "";
}

export type LoopPairing = {
  pairs: LoopPair[];
  unpairedStartIds: string[];
  unpairedEndIds: string[];
  duplicateEnds: Array<{ startId: string; endIds: string[] }>;
};

export function resolveLoopPairing(graph: LoopGraph | null | undefined): LoopPairing {
  const nodes = loopGraphNodes(graph);
  const order = [...nodes.keys()];
  const startIds = order.filter((id) => nodes.get(id)?.type === "loop_start");
  const endIds = order.filter((id) => nodes.get(id)?.type === "loop_end");

  const claimsByStart = new Map<string, string[]>();
  const unpairedEndIds: string[] = [];
  for (const endId of endIds) {
    const ref = loopStartRefOf(nodes.get(endId));
    if (!ref || nodes.get(ref)?.type !== "loop_start") {
      unpairedEndIds.push(endId);
      continue;
    }
    const list = claimsByStart.get(ref) ?? [];
    list.push(endId);
    claimsByStart.set(ref, list);
  }

  const pairs: LoopPair[] = [];
  const unpairedStartIds: string[] = [];
  const duplicateEnds: Array<{ startId: string; endIds: string[] }> = [];
  for (const startId of startIds) {
    const claimants = claimsByStart.get(startId) ?? [];
    if (claimants.length === 0) {
      unpairedStartIds.push(startId);
      continue;
    }
    if (claimants.length > 1) {
      duplicateEnds.push({ startId, endIds: claimants });
      continue;
    }
    pairs.push({ startId, endId: claimants[0]! });
  }
  return { pairs, unpairedStartIds, unpairedEndIds, duplicateEnds };
}

export function resolveLoopPairs(graph: LoopGraph | null | undefined): LoopPair[] {
  return resolveLoopPairing(graph).pairs;
}

export function loopBody(graph: LoopGraph | null | undefined, startId: string, endId: string): Set<string> {
  const forwardAdj = buildForwardAdjacency(graphEdges(graph));
  const fromStart = descendants(forwardAdj, startId);
  const afterEnd = descendants(forwardAdj, endId);
  const body = new Set<string>();
  for (const id of fromStart) {
    if (id === endId) continue;
    if (afterEnd.has(id)) continue;
    body.add(id);
  }
  body.delete(startId);
  return body;
}

export type LoopPairMembership = { pair: LoopPair; pairIndex: number };

/**
 * node id → the loop pair that owns it, for every step inside a body.
 *
 * First-pair-wins on an overlap. The hub validator refuses overlapping bodies,
 * but the canvas renders graphs that have not been saved yet, let alone
 * validated — so ownership must not depend on iteration order while the user
 * is mid-edit.
 */
export function nodeToLoopPair(graph: LoopGraph | null | undefined): Map<string, LoopPairMembership> {
  const pairs = resolveLoopPairs(graph);
  const owner = new Map<string, LoopPairMembership>();
  pairs.forEach((pair, pairIndex) => {
    for (const nodeId of loopBody(graph, pair.startId, pair.endId)) {
      if (owner.has(nodeId)) continue;
      owner.set(nodeId, { pair, pairIndex });
    }
  });
  return owner;
}

/**
 * The pair name shown on both bookends, derived from the START step's LABEL.
 *
 * Never a node id: a UUID on screen is always wrong, and the pairing reference
 * is a node id, so the one thing the user must not see is the very thing the
 * relationship is stored as.
 */
export function loopPairName(startLabel: string | null | undefined, which: "start" | "end"): string {
  const base = (startLabel ?? "").trim().replace(/^Loop:\s*/i, "").replace(/^End:\s*/i, "");
  if (!base) return which === "start" ? "Loop" : "End of loop";
  return which === "start" ? `Loop: ${base}` : `End: ${base}`;
}

/**
 * The DECORATIVE link drawn between a loop's bookends.
 *
 * It is not a graph edge. The pair is joined by a config reference precisely
 * so the stored graph stays a plain acyclic DAG, and drawing that relationship
 * must not smuggle a connection back into the spec: an edge here would reach
 * the validator (which refuses it), the saved spec, lineage, and the change
 * grammar as a connection the user never made.
 *
 * Kept as a pure builder so the constraint is testable rather than a habit —
 * every id it mints carries `LOOP_PAIR_LINK_PREFIX`, which the persistence
 * gate asserts never appears in a spec edge.
 */
export const LOOP_PAIR_LINK_PREFIX = "loop-pair-link:";

export function loopPairLinkId(pair: LoopPair): string {
  return `${LOOP_PAIR_LINK_PREFIX}${pair.startId}:${pair.endId}`;
}

export function isLoopPairLinkId(edgeId: string): boolean {
  return edgeId.startsWith(LOOP_PAIR_LINK_PREFIX);
}
